import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import {
  FeedbackEntity,
  FeedbackTryEntry,
  MotivatingFeedbackEntry,
} from '../feedback.entity';
import { MatDividerModule } from '@angular/material/divider';
import { TranslocoModule } from '@jsverse/transloco';
import { CdkDragDrop, DragDropModule, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { SubjectEntity } from '../../subject/subject.entity';

export interface FeedbackRow {
  subjectId: string
  subjectName: string
}

export interface FeedbackDialogData extends Partial<FeedbackEntity> {
  freeSubjects?: SubjectEntity[]
}

@Component({
  selector: 'pixl-edit-feedback',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatIconModule,
    ReactiveFormsModule,
    MatDividerModule,
    TranslocoModule,
    DragDropModule,
  ],
  templateUrl: './edit-feedback.component.html',
  styleUrl: './edit-feedback.component.scss',
})
export class EditFeedbackComponent {
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef<EditFeedbackComponent, FeedbackEntity | undefined>);
  protected readonly data = inject<FeedbackDialogData | null>(MAT_DIALOG_DATA, { optional: true });

  protected readonly freeSubjects = this.data?.freeSubjects ?? [];

  protected firstTryEntries = this.normalizeTryEntries(this.data?.firstTry);
  protected secondTryEntries = this.normalizeTryEntries(this.data?.secondTry);
  private readonly motivatingValues = this.normalizeMotivatingEntries(this.data?.motivating);

  public tileDrafts: Record<string, string> = {};

  protected readonly formGroup = this.fb.group({
    subjectId: [this.data?.subjectId ?? '', !this.data?.id ? Validators.required : []],
    changedBy: [this.data?.changedBy ?? ''],
    version: [this.data?.version ?? 1, [Validators.required, Validators.min(1)]],
    motivatingDraft: this.createMotivatingGroup(),
    motivating: this.fb.array(
      this.motivatingValues.map((entry) => this.createMotivatingGroup(entry, true)),
    ),
  });

  get motivatingArray(): FormArray<FormGroup> {
    return this.formGroup.get('motivating') as FormArray<FormGroup>;
  }

  get motivatingDraftGroup(): FormGroup {
    return this.formGroup.get('motivatingDraft') as FormGroup;
  }

  get firstTryDropListIds(): string[] {
    return this.firstTryEntries.map((_, i) => `firstTry-${i}`);
  }

  get secondTryDropListIds(): string[] {
    return this.secondTryEntries.map((_, i) => `secondTry-${i}`);
  }

  protected getTileKey(prefix: string, index: number): string {
    return `${prefix}-${index}`;
  }

  public addTileMessage(entries: FeedbackTryEntry[], index: number, prefix: string): void {
    const key = this.getTileKey(prefix, index);
    const draftText = (this.tileDrafts[key] || '').trim();

    if (!draftText) return;

    if (!entries[index].message) {
      entries[index].message = [];
    }

    entries[index].message.push(draftText);
    this.tileDrafts[key] = '';
  }

  public removeTileMessage(entries: FeedbackTryEntry[], tileIndex: number, messageIndex: number): void {
    entries[tileIndex].message.splice(messageIndex, 1);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected trackByMessage(index: number, item: string): number {
    return index;
  }

  public addMotivatingText(): void {
    const draft = this.normalizeMotivatingEntry(this.motivatingDraftGroup.getRawValue());

    if (!draft.text) {
      return;
    }

    this.motivatingArray.push(this.createMotivatingGroup(draft, true));
    this.motivatingDraftGroup.controls.text.setValue('');
  }

  public removeMotivatingText(index: number): void {
    this.motivatingArray.removeAt(index);
  }

  public getMotivatingEntry(index: number): FormGroup {
    return this.motivatingArray.at(index) as FormGroup;
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public onSave(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const updatedEntity = Object.assign(new FeedbackEntity(), this.data ?? {}, {
      id: this.data?.id,
      subjectId: this.formGroup.controls.subjectId.value,
      firstTry: this.firstTryEntries,
      secondTry: this.secondTryEntries,
      changedBy: this.formGroup.controls.changedBy.value,
      version: this.formGroup.controls.version.value,
      motivating: this.motivatingArray.controls
        .map((control) => this.normalizeMotivatingEntry(control.getRawValue()))
        .filter((entry) => entry.text.length > 0)
        .map((entry) => entry.text),
    });

    this.dialogRef.close(updatedEntity);
  }

  protected isRangeEmpty(entry: FeedbackTryEntry): boolean {
    return !entry.message || entry.message.length === 0;
  }

  public splitRange(entries: FeedbackTryEntry[], index: number, end: boolean): void {
    const item = entries[index];
    const from = item.from;
    const to = item.to;

    if (from === to) return;

    let size = to - from + 1;
    if (size % 2 === 1) {
      size--;
    }

    const newRangeSize = Math.floor(size / 2);

    if (end) {
      const newRangeFrom = to - newRangeSize + 1;
      const newRange: FeedbackTryEntry = {
        from: newRangeFrom,
        to: to,
        message: []
      };
      item.to = newRangeFrom - 1;
      entries.splice(index + 1, 0, newRange);
    }
    else {
      const newRangeTo = from + newRangeSize - 1;
      const newRange: FeedbackTryEntry = {
        from: from,
        to: newRangeTo,
        message: []
      };
      item.from = newRangeTo + 1;

      entries.splice(index, 0, newRange);
    }
  }

  public deleteRange(entries: FeedbackTryEntry[], index: number): void {
    if (entries.length <= 1) return;

    const removed = entries[index];
    if (index > 0) {
      entries[index - 1].to = removed.to;
    }
    else {
      entries[index + 1].from = removed.from;
    }
    entries.splice(index, 1);
  }

  public adjustScale(entries: FeedbackTryEntry[], index: number, change: number): void {
    const current = entries[index];

    if (index === entries.length - 1) {
      const prev = entries[index - 1];
      if (!prev) return;

      if (prev.to + change >= prev.from && prev.to + change < current.from) {
        prev.to += change;
        current.from += change;
      }
    }
    else {
      const next = entries[index + 1];
      if (current.to + change >= current.from && current.to + change < next.to) {
        current.to += change;
        next.from += change;
      }
    }
  }

  private createMotivatingGroup(
    entry: Partial<MotivatingFeedbackEntry> = {},
    requireText = false,
  ): FormGroup {
    return this.fb.nonNullable.group({
      text: [entry.text ?? '', requireText ? Validators.required : []],
      color: [''],
      fontSize: [''],
    });
  }

  private normalizeTryEntries(value: unknown): FeedbackTryEntry[] {
    if (typeof value === 'string') {
      const text = value.trim();
      return text ? [{ from: 0, to: 0, message: [text] }] : [{ from: 0, to: 100, message: [] }];
    }

    if (!Array.isArray(value) || value.length === 0) {
      return [{ from: 0, to: 100, message: [] }];
    }

    return value.map((entry) => this.normalizeTryEntry(entry));
  }

  private normalizeTryEntry(value: unknown): FeedbackTryEntry {
    if (!value || typeof value !== 'object') {
      return {
        from: 0,
        to: 0,
        message: []
      };
    }

    const entry = value as Partial<FeedbackTryEntry> & { message?: unknown };

    return {
      from: Number.isFinite(entry.from) ? Number(entry.from) : 0,
      to: Number.isFinite(entry.to) ? Number(entry.to) : 0,
      message: this.normalizeTryMessages(entry.message),
    };
  }

  private normalizeTryMessages(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return typeof value === 'string' && value.trim() ? [value.trim()] : [];
    }

    return value
      .filter((message): message is string => typeof message === 'string')
      .map((message) => message.trim())
      .filter((message) => message.length > 0);
  }

  private normalizeMotivatingEntries(value: unknown): MotivatingFeedbackEntry[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((entry) => this.normalizeMotivatingEntry(entry))
      .filter((entry) => entry.text.length > 0);
  }

  private normalizeMotivatingEntry(value: unknown): MotivatingFeedbackEntry {
    if (typeof value === 'string') {
      return {
        text: value.trim(),
        color: '',
        fontSize: '',
      };
    }

    if (value && typeof value === 'object') {
      const entry = value as Partial<MotivatingFeedbackEntry> & { message?: string };

      return {
        text: (entry.text ?? entry.message ?? '').trim(),
        color: '',
        fontSize: '',
      };
    }

    return {
      text: '',
      color: '',
      fontSize: '',
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public dropMessageAcrossRanges(event: CdkDragDrop<string[]>, _targetEntry: FeedbackTryEntry): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    }
    else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
    }
  }
}
